import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { DirectoryNode, FileNode } from "@/shared/domain/files/filesystem";

import { type SortColumn, type SortDirection } from "../FileService";
import { type FileSystemNode } from "../filesystem";
import { FileSystemNodeType } from "../filesystem/fileSystemNode";

import { fileQueryKeys } from "./fileQueryKeys";

export function useDirectoryContents(
  directoryPath: string,
  datasetId: string,
  resourceOwnerId?: string,
  pageSize: number = 25,
  nextToken?: string,
  searchTerm?: string,
  showHiddenFiles: boolean = false,
  extensions?: string[],
  sortColumn?: SortColumn,
  sortDirection?: SortDirection,
) {
  const { files } = useDomainServices();

  return useQuery<
    {
      items: FileSystemNode[];
      nextToken: string | undefined;
    },
    Error
  >({
    placeholderData: (items) => {
      // return the items from the previous query while we wait for updated results
      // prevents user from seeing table skeleton during pagination and sorting
      return items;
    },
    queryKey: fileQueryKeys.directoryContents({
      directoryPath,
      datasetId,
      nextToken,
      pageSize,
      searchTerm,
      showHiddenFiles,
      extensions,
      sortColumn,
      sortDirection,
    }),
    queryFn: async ({ signal }) => {
      if (searchTerm) {
        const data = await files.getFilesForDirectory({
          path: directoryPath,
          fileNameSearchTerm: searchTerm,
          datasetId,
          pageSize,
          nextToken,
          showHiddenFiles,
          extensions,
          sortColumn,
          sortDirection,
          options: {
            abortSignal: signal,
            resourceOwnerId,
          },
        });

        const items: FileNode[] = data.items.map((item) => {
          return {
            type: FileSystemNodeType.File,
            name: item.name,
            file: item,
          };
        });

        return {
          items,
          nextToken: data.next_token ?? undefined,
        };
      }

      const directoryContents = await files.getItemsForDirectory({
        directoryPath,
        datasetId,
        pageSize,
        nextToken,
        showHiddenFiles,
        extensions,
        sortColumn,
        sortDirection,
        options: {
          abortSignal: signal,
          resourceOwnerId,
        },
      });

      const directories: DirectoryNode[] = directoryContents.directories.map(
        (item) => {
          return {
            type: FileSystemNodeType.Directory,
            name: item.name,
            directory: item,
          };
        },
      );

      const fileItems: FileNode[] = directoryContents.files.map((item) => {
        return {
          type: FileSystemNodeType.File,
          name: item.relative_path.split("/").slice(-1)[0],
          file: item,
        };
      });

      const items = [...directories, ...fileItems];

      return {
        items,
        nextToken: directoryContents.next_token ?? undefined,
      };
    },
    // Always refetch when navigating back to the page
    // So we can show up to date file records modified by other processes
    staleTime: 0,
  });
}
